<script setup lang="ts">
/**
 * Presentational list. Receives rows + pagination + filter-count via
 * props; emits row-click / update:page / filter-click. No queries, no
 * mutations, no router pushes — orchestration lives in the container.
 *
 * Filter trigger lives on `DataTableV2` via `:filterable="true"` +
 * `:filter-count` + `@filter-click`. Search is intentionally off — the
 * filter drawer carries name / email filters.
 */
import Box from '@uiKit/elements/Box.vue'
import Text from '@uiKit/elements/Text.vue'
import DataTableV2, { type TableColumn } from '@uiKit/components/DataTableV2.vue'
import CopyButton from '@uiKit/components/CopyButton.vue'
import { formatDate, formatMoney } from '@shared/utils'
import type { DepositRequestRow } from '../types/depositRequestsTypes'

defineProps<{
  rows: DepositRequestRow[]
  loading: boolean
  total: number
  page: number
  pageSize: number
  filterCount: number
  emptyTitle?: string
  emptySubtitle?: string
  title?: string
  subtitle?: string
  search?: string
}>()

const emit = defineEmits<{
  (e: 'update:page', value: number): void
  (e: 'update:pageSize', value: number): void
  (e: 'row-click', row: DepositRequestRow): void
  (e: 'filter-click'): void
  (e: 'update:search', value: string): void
}>()

const columns: TableColumn[] = [
  { key: 'investor', label: 'INVESTOR', width: '180px' },
  { key: 'amount', label: 'AMOUNT', width: '140px', align: 'right' },
  { key: 'paymentReference', label: 'PAYMENT REF', width: 'minmax(180px, 1.2fr)' },
  { key: 'createdAt', label: 'SUBMITTED', width: 'minmax(140px, 1fr)' },
]

function formatAmount(row: DepositRequestRow): string {
  return formatMoney(row.currencyCode, row.amount)
}
</script>

<template>
  <DataTableV2
    :columns="columns"
    :rows="rows"
    :loading="loading"
    row-key="id"
    :title="title"
    :subtitle="subtitle"
    :search="search"
    search-placeholder="Search by investor name…"
    :local-search="false"
    :local-pagination="false"
    :filterable="true"
    :filter-count="filterCount"
    :page="page"
    :page-size="pageSize"
    :total="total"
    :url-sync="true"
    page-size-storage-key="treasury.depositRequests.pageSize"
    clickable
    :empty-title="emptyTitle ?? 'No deposit requests'"
    :empty-subtitle="emptySubtitle ?? 'Requests matching your filters will appear here.'"
    @update:page="emit('update:page', $event)"
    @update:page-size="emit('update:pageSize', $event)"
    @update:search="emit('update:search', $event)"
    @filter-click="emit('filter-click')"
    @row-click="emit('row-click', $event as DepositRequestRow)"
  >
    <template #cell-investor="{ row }">
      <Box flex direction="col">
        <Text type="body" weight="semibold">{{
          (row as DepositRequestRow).investorName || '—'
        }}</Text>
        <Text type="caption" color="muted">
          {{ (row as DepositRequestRow).investorEmail || '—' }}
        </Text>
      </Box>
    </template>

    <template #cell-amount="{ row }">
      <Text
        type="body-sm"
        weight="semibold"
        align="right"
        style="font-variant-numeric: tabular-nums"
      >
        {{ formatAmount(row as DepositRequestRow) }}
      </Text>
    </template>

    <template #cell-utrNumber="{ row }">
      <Box v-if="(row as DepositRequestRow).utrNumber" flex align="center" gap="2">
        <Text type="body-sm" color="muted">
          {{ (row as DepositRequestRow).utrNumber }}
        </Text>
        <CopyButton
          :value="(row as DepositRequestRow).utrNumber ?? ''"
          label="Copy"
          copied-label="Copied"
        />
      </Box>
      <Text v-else type="body-sm" color="muted">—</Text>
    </template>

    <template #cell-createdAt="{ row }">
      <Text type="body-sm" color="muted">{{
        formatDate((row as DepositRequestRow).createdAt)
      }}</Text>
    </template>
  </DataTableV2>
</template>
